/**
 * BookAppointmentModal.jsx
 * src/components/user/BookAppointmentModal.jsx
 *
 * Changes:
 *  - All Telegram / BotFather code removed
 *  - Clicking a doctor auto-advances to Step 2
 *  - Clicking a time slot auto-advances to Step 3
 *  - Step tabs are clickable with guards:
 *      Step 2/3 tab clicked without doctor → warning + stay on Step 1
 *      Step 3 tab clicked without slot    → warning + stay on Step 2
 *  - Dynamic doctors from API
 *  - Dynamic booked slots per doctor+date (booked slots hidden)
 *  - FIXED: Defer navigate() call using useEffect
 */

import { useState, useEffect, useRef } from "react";
import { useNavigate }         from "react-router-dom";
import { useUserAuth }         from "../../context/UserAuthContext";
import { apiFetch }            from "../helpers";

/* ── week days ── */
function getWeekDays() {
  const days = [];
  const now  = new Date();
  for (let i = 0; i < 7; i++) {
    const d = new Date(now);
    d.setDate(now.getDate() + i);
    days.push({
      date: d,
      label: d.toLocaleDateString("en-US", { weekday: "short" }),
      num: d.getDate(),
      isToday: i === 0,
      dateStr: d.toISOString().split("T")[0],
      displayDate: d.toLocaleDateString("en-US", { weekday:"short", day:"2-digit", month:"short", year:"numeric" }).toUpperCase(),
    });
  }
  return days;
}
const WEEK_DAYS = getWeekDays();

/* ── 15-min slots 08:00–22:45  |  8 cols/row = 2 hours per row ── */
function generateAllSlots() {
  const slots = [];
  for (let h = 8; h < 23; h++) {
    for (let m = 0; m < 60; m += 15) {
      const hour = h > 12 ? h - 12 : h === 0 ? 12 : h;
      const ampm = h >= 12 ? "PM" : "AM";
      const min  = String(m).padStart(2, "0");
      slots.push({ label:`${String(hour).padStart(2,"0")}:${min} ${ampm}`, time24:`${String(h).padStart(2,"0")}:${min}` });
    }
  }
  return slots;
}
const ALL_SLOTS = generateAllSlots();

const inp = { width:"100%", padding:"10px 13px", borderRadius:8, border:"1.5px solid #E4EAF0", fontSize:13, outline:"none", background:"#F8FAFC", color:"#0A1628", fontFamily:"inherit", transition:"border .2s" };
const onFocusInp = e => { e.target.style.borderColor="#1D9E75"; e.target.style.background="#fff"; };
const onBlurInp  = e => { e.target.style.borderColor="#E4EAF0"; e.target.style.background="#F8FAFC"; };

/* ────────────── STEP TABS ────────────── */
function StepTabs({ step, doctor, slotObj, onGoToStep }) {
  const tabs = [["Step 1-Doctor List",1],["Step 2-Appointment Date",2],["Step 3-Fill Details",3]];
  return (
    <div style={{ display:"flex", gap:8, marginBottom:18, flexWrap:"wrap" }}>
      {tabs.map(([label,n]) => {
        const isActive = step===n, isDone = step>n;
        const canClick = n===1 || (n===2&&!!doctor) || (n===3&&!!doctor&&!!slotObj);
        return (
          <div key={n} onClick={() => onGoToStep(n)}
            title={n===2&&!doctor?"Select a doctor first":n===3&&!slotObj?"Select a slot first":""}
            style={{ padding:"7px 18px", borderRadius:6, fontSize:13, fontWeight:500, userSelect:"none",
              cursor:canClick?"pointer":"not-allowed", opacity:(!canClick&&!isActive)?0.55:1,
              background:isActive?"#E8323C":isDone?"#E1F5EE":"#F0F4F8",
              color:isActive?"#fff":isDone?"#085041":"#6B7A8D",
              border:`1px solid ${isActive?"#E8323C":isDone?"#9FE1CB":"#E0E5EC"}`, transition:"all .15s" }}>
            {isDone&&!isActive?"✓ ":""}{label}
          </div>
        );
      })}
    </div>
  );
}

function ClinicHeader() {
  return (
    <div style={{ marginBottom:16, paddingBottom:14, borderBottom:"1px solid #F0F4F8" }}>
      <h6 style={{ fontSize:16, fontWeight:700, color:"#0A1628", margin:"0 0 3px" }}>BrightSmile Dental Clinic</h6>
      <p style={{ fontSize:13, color:"#6B7A8D", margin:0 }}>306/7 Earth Essence, Hebatpur Road, Ahmedabad</p>
    </div>
  );
}

/* ────────────── STEP 1 ────────────── */
function Step1DoctorList({ selectedDoctor, onSelect }) {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  useEffect(() => {
    (async () => {
      try {
        const json = await apiFetch(`${process.env.REACT_APP_GET_ACTIVE_DOCTOR}`);
        if (json.status) setDoctors(json.data || []);
        else setError("Could not load doctors.");
      } catch(e) { console.error(e); setError("Network error loading doctors."); }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div style={{ textAlign:"center", padding:"2.5rem" }}><div className="spinner-border" style={{ color:"#1D9E75" }} /><p style={{ fontSize:13, color:"#6B7A8D", marginTop:10 }}>Loading doctors...</p></div>;
  if (error)   return <div style={{ background:"#FCEBEB", borderRadius:10, padding:"12px 16px", fontSize:13, color:"#501313" }}>{error}</div>;
  if (!doctors.length) return <p style={{ textAlign:"center", color:"#6B7A8D", padding:"2rem 0" }}>No doctors available.</p>;

  return (
    <div>
      <div style={{ background:"#F0FFF8", border:"1px solid #9FE1CB", borderRadius:8, padding:"8px 14px", marginBottom:12, fontSize:12, color:"#085041", display:"flex", alignItems:"center", gap:8 }}>
        <span>👆</span> Click on a doctor to proceed to date &amp; time selection
      </div>
      <div style={{ maxHeight:370, overflowY:"auto" }}>
        {doctors.map(doc => {
          const isSel = selectedDoctor?.doctor_id === doc.doctor_id;
          return (
            <div key={doc.doctor_id} onClick={() => onSelect(doc)}
              style={{ display:"flex", alignItems:"center", gap:14, padding:"14px 16px", cursor:"pointer", borderBottom:"1px solid #F0F4F8",
                background:isSel?"#F0FFF8":"#fff", borderLeft:isSel?"3px solid #1D9E75":"3px solid transparent", transition:"all .15s" }}
              onMouseEnter={e=>{ if(!isSel) e.currentTarget.style.background="#FAFCFF"; }}
              onMouseLeave={e=>{ if(!isSel) e.currentTarget.style.background="#fff"; }}>
              <div style={{ width:54, height:54, borderRadius:"50%", background:"#E1F5EE", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, fontWeight:700, color:"#085041", flexShrink:0, border:`2px solid ${isSel?"#1D9E75":"#9FE1CB"}`, overflow:"hidden" }}>
                {doc.profile_image ? <img src={`/uploads/${doc.profile_image}`} alt={doc.name} style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : doc.name.split(" ").map(n=>n[0]).join("").slice(0,2)}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:700, fontSize:14, color:"#0A1628" }}>{doc.name}</div>
                <div style={{ fontSize:12, color:"#6B7A8D", display:"flex", alignItems:"center", gap:4, marginTop:2 }}><span>🏠</span> 306/7 EARTH ESSENCE, Hebatpur Road</div>
                <span style={{ display:"inline-block", marginTop:5, fontSize:10, fontWeight:600, background:"#E1F5EE", color:"#085041", padding:"2px 8px", borderRadius:10 }}>{doc.specialization}</span>
              </div>
              {isSel ? <span style={{ color:"#1D9E75", fontSize:22, fontWeight:700 }}>✓</span> : <span style={{ color:"#C0D8D8", fontSize:20 }}>›</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ────────────── STEP 2 ────────────── */
function Step2DateSlot({ doctor, selectedDay, selectedSlot, onDayChange, onSlotSelect, onPrev }) {
  const [bookedSlots,  setBookedSlots]  = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => {
    if (!doctor?.doctor_id) return;
    (async () => {
      setLoadingSlots(true);
      try {
        const json = await apiFetch(`${process.env.REACT_APP_GET_BOOKED_APPOINTMENT}`, { method:"POST", body:{ doctor_id:doctor.doctor_id, appointment_date:selectedDay.dateStr } });
        if (json.status && Array.isArray(json.data)) {
          const times = json.data.map(appt => {
            const t = appt.appointment_time || "";
            return t.length >= 5 ? t.slice(0, 5) : t;
          }).filter(Boolean);
          setBookedSlots(times);
        } else {
          setBookedSlots([]);
        }
      } catch { setBookedSlots([]); }
      setLoadingSlots(false);
    })();
  }, [doctor?.doctor_id, selectedDay.dateStr]);

  const bookedSet = new Set(bookedSlots);
  const isToday = selectedDay.dateStr === new Date().toISOString().split("T")[0];
  const now     = new Date();
  const currentTime24 = `${String(now.getHours()).padStart(2,"0")}:${String(now.getMinutes()).padStart(2,"0")}`;

  const allSlotsWithStatus = ALL_SLOTS.map(s => ({
    ...s,
    isBooked: bookedSet.has(s.time24),
    isPast:   isToday && s.time24 <= currentTime24,
  }));
  const availableCount = allSlotsWithStatus.filter(s => !s.isBooked && !s.isPast).length;

  return (
    <div>
      <div style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 12px", marginBottom:14, background:"#E1F5EE", border:"1px solid #9FE1CB", borderRadius:8 }}>
        <span style={{ fontSize:18 }}>👨‍⚕️</span>
        <span style={{ fontSize:12, color:"#085041", fontWeight:700 }}>{doctor.name}</span>
        <span style={{ fontSize:11, color:"#6B7A8D", marginLeft:2 }}>— {doctor.specialization}</span>
      </div>

      {/* Day selector */}
      <div style={{ display:"grid", gridTemplateColumns:`repeat(${WEEK_DAYS.length},1fr)`, background:"#1B2A6B", borderRadius:"8px 8px 0 0" }}>
        {WEEK_DAYS.map(day => (
          <div key={day.dateStr} onClick={() => onDayChange(day)}
            style={{ textAlign:"center", padding:"10px 4px", cursor:"pointer",
              background:selectedDay.dateStr===day.dateStr?"#1D9E75":day.isToday?"#243580":"transparent",
              color:"#fff", borderRight:"1px solid rgba(255,255,255,.1)", transition:"all .15s" }}>
            <div style={{ fontSize:10, opacity:.85 }}>{day.isToday?"Today ":""}{day.label}</div>
            <div style={{ fontSize:16, fontWeight:700 }}>{day.num}</div>
          </div>
        ))}
      </div>

      {/* Slot grid */}
      <div style={{ border:"1px solid #E4EAF0", borderTop:"none", borderRadius:"0 0 8px 8px", padding:"12px 14px", maxHeight:280, overflowY:"auto", minHeight:160 }}>
        {loadingSlots ? (
          <div style={{ textAlign:"center", padding:"2rem" }}>
            <div className="spinner-border spinner-border-sm" style={{ color:"#1D9E75" }} />
            <p style={{ fontSize:12, color:"#6B7A8D", marginTop:8, marginBottom:0 }}>Loading available slots...</p>
          </div>
        ) : availableCount === 0 ? (
          <div style={{ textAlign:"center", padding:"2rem", color:"#6B7A8D" }}>
            <div style={{ fontSize:32, marginBottom:8 }}>😔</div>
            <p style={{ fontSize:13, margin:0 }}>No available slots for this day.<br/>Please try another date.</p>
          </div>
        ) : (
          <>
            {/* Legend + count */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10, flexWrap:"wrap", gap:6 }}>
              <span style={{ fontSize:11, color:"#6B7A8D", display:"flex", alignItems:"center", gap:5 }}>
                <span>👆</span> Click a time to proceed to patient details
              </span>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                {isToday && (
                  <span style={{ display:"flex", alignItems:"center", gap:4, fontSize:11, color:"#6B7A8D" }}>
                    <span style={{ width:12, height:12, borderRadius:3, background:"#F7F9FB", border:"1px dashed #D8E0EA", display:"inline-block" }} />
                    Past
                  </span>
                )}
                <span style={{ display:"flex", alignItems:"center", gap:4, fontSize:11, color:"#6B7A8D" }}>
                  <span style={{ width:12, height:12, borderRadius:3, background:"#F0F4F8", border:"1px solid #CBD5DE", display:"inline-block" }} />
                  Booked
                </span>
                <span style={{ display:"flex", alignItems:"center", gap:4, fontSize:11, color:"#6B7A8D" }}>
                  <span style={{ width:12, height:12, borderRadius:3, background:"transparent", border:"1px solid rgba(29,158,117,.45)", display:"inline-block" }} />
                  Available
                </span>
                {bookedSlots.length > 0 && (
                  <span style={{ fontSize:11, color:"#9BA8B5" }}>
                    {bookedSlots.length} booked · {availableCount} free
                  </span>
                )}
              </div>
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"repeat(8,1fr)", gap:6 }}>
              {allSlotsWithStatus.map((slot, i) => {
                const isSel    = selectedSlot?.time24 === slot.time24;
                const isBooked = slot.isBooked;
                const isPast   = slot.isPast;

                if (isPast && !isBooked) {
                  return (
                    <div key={i} title="Time already passed"
                      style={{
                        padding:"6px 2px", textAlign:"center", borderRadius:6,
                        fontSize:11, fontWeight:400, cursor:"not-allowed",
                        color:"#C0C8D2", background:"#F7F9FB",
                        border:"1px dashed #D8E0EA",
                        opacity:0.6,
                      }}>
                      {slot.label}
                    </div>
                  );
                }

                if (isBooked) {
                  return (
                    <div key={i} title="Already booked"
                      style={{
                        padding:"6px 2px", textAlign:"center", borderRadius:6,
                        fontSize:11, fontWeight:400, cursor:"not-allowed",
                        color:"#A0ADB9", background:"#F0F4F8",
                        border:"1px solid #CBD5DE",
                        textDecoration:"line-through",
                        opacity:0.72, position:"relative",
                      }}>
                      {slot.label}
                      <span style={{ position:"absolute", top:1, right:3, fontSize:8, color:"#B0BAC5" }}>🔒</span>
                    </div>
                  );
                }

                return (
                  <div key={i} onClick={() => onSlotSelect(slot)}
                    style={{
                      padding:"6px 2px", textAlign:"center", borderRadius:6,
                      fontSize:11, fontWeight:500, cursor:"pointer",
                      color:isSel?"#fff":"#1D9E75",
                      background:isSel?"#1D9E75":"transparent",
                      border:`1px solid ${isSel?"#1D9E75":"rgba(29,158,117,.25)"}`,
                      transition:"all .15s",
                    }}
                    onMouseEnter={e=>{ if(!isSel) e.currentTarget.style.background="#E1F5EE"; }}
                    onMouseLeave={e=>{ if(!isSel) e.currentTarget.style.background="transparent"; }}>
                    {slot.label}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <div style={{ marginTop:14 }}>
        <button onClick={onPrev} style={{ padding:"9px 24px", background:"#E8323C", color:"#fff", border:"none", borderRadius:6, fontSize:13, fontWeight:700, cursor:"pointer" }}>Previous</button>
      </div>
    </div>
  );
}

/* ────────────── STEP 3 ────────────── */
function Step3Details({ doctor, slot, onPrev, onSubmit, user }) {
  const [form, setForm] = useState({ name:user?.name||"", email:user?.email||"", mobile:user?.mobile||"", reason:"" });
  const [error,      setError]   = useState("");
  const [submitting, setSub]     = useState(false);

  const handleSubmit = async () => {
    if (!form.name.trim())       return setError("Patient name is required");
    if (!form.mobile.trim())     return setError("Mobile number is required");
    if (form.mobile.length < 10) return setError("Enter a valid 10-digit mobile number");
    setSub(true); setError("");
    try {
      const json = await apiFetch(`${process.env.REACT_APP_BOOK_APPOINTMENT}`, {
        method:"POST",
        body:{ patient_id:user?.user_id||null, doctor_id:doctor.doctor_id, appointment_date:slot.day.dateStr, appointment_time:slot.slot.time24, patient_name:form.name.trim(), email:form.email.trim(), mobile:form.mobile.trim(), notes:form.reason.trim() },
      });
      if (json.status) onSubmit(json);
      else setError(json.message || "Booking failed. Please try again.");
    } catch(e) { console.error(e); setError("Network error. Please try again."); }
    setSub(false);
  };

  return (
    <div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", borderRadius:8, overflow:"hidden", marginBottom:14 }}>
        <div style={{ background:"#1B2A6B", color:"#fff", textAlign:"center", padding:"12px 8px" }}>
          <div style={{ fontSize:10, opacity:.7, letterSpacing:1 }}>DATE</div>
          <div style={{ fontSize:14, fontWeight:700 }}>{slot.day.displayDate}</div>
        </div>
        <div style={{ background:"#1B2A6B", color:"#fff", textAlign:"center", padding:"12px 8px", borderLeft:"1px solid rgba(255,255,255,.15)" }}>
          <div style={{ fontSize:10, opacity:.7, letterSpacing:1 }}>TIME</div>
          <div style={{ fontSize:14, fontWeight:700 }}>{slot.slot.label}</div>
        </div>
      </div>

      <div style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 12px", background:"#E1F5EE", border:"1px solid #9FE1CB", borderRadius:8, marginBottom:16 }}>
        <span>👨‍⚕️</span>
        <span style={{ fontSize:12, color:"#085041", fontWeight:700 }}>{doctor.name}</span>
        <span style={{ fontSize:11, color:"#6B7A8D" }}>— {doctor.specialization}</span>
      </div>

      <div style={{ marginBottom:14 }}>
        <label style={{ fontSize:12, fontWeight:700, color:"#0A1628", display:"block", marginBottom:5 }}>Patient/Visitor Name</label>
        <input style={inp} placeholder="Please enter your full name." value={form.name} onChange={e=>setForm({...form,name:e.target.value})} onFocus={onFocusInp} onBlur={onBlurInp} />
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
        <div>
          <label style={{ fontSize:12, fontWeight:700, color:"#0A1628", display:"block", marginBottom:5 }}>Email Address</label>
          <input type="email" style={inp} placeholder="Please enter your email id." value={form.email} onChange={e=>setForm({...form,email:e.target.value})} onFocus={onFocusInp} onBlur={onBlurInp} />
        </div>
        <div>
          <label style={{ fontSize:12, fontWeight:700, color:"#0A1628", display:"block", marginBottom:5 }}>Mobile Number</label>
          <div style={{ display:"flex" }}>
            <span style={{ padding:"10px 10px", background:"#F0F4F8", border:"1.5px solid #E4EAF0", borderRadius:"8px 0 0 8px", fontSize:13, display:"flex", alignItems:"center", gap:4, flexShrink:0 }}>🇮🇳 +91</span>
            <input type="tel" style={{ ...inp, borderRadius:"0 8px 8px 0", borderLeft:"none", flex:1 }} placeholder="Mobile number" value={form.mobile} onChange={e=>setForm({...form,mobile:e.target.value.replace(/\D/,"").slice(0,10)})} onFocus={onFocusInp} onBlur={onBlurInp} />
          </div>
        </div>
      </div>

      <div style={{ marginBottom:16 }}>
        <label style={{ fontSize:12, fontWeight:700, color:"#0A1628", display:"block", marginBottom:5 }}>Appointment Reason</label>
        <textarea style={{ ...inp, height:80, resize:"none" }} placeholder="Briefly describe your concern..." value={form.reason} onChange={e=>setForm({...form,reason:e.target.value})} onFocus={onFocusInp} onBlur={onBlurInp} />
      </div>

      {error && <div style={{ background:"#FCEBEB", border:"1px solid #F09595", borderRadius:8, padding:"10px 13px", marginBottom:12, fontSize:12, color:"#501313" }}>⚠️ {error}</div>}

      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <button onClick={onPrev} style={{ padding:"9px 24px", background:"#E8323C", color:"#fff", border:"none", borderRadius:6, fontSize:13, fontWeight:700, cursor:"pointer" }}>Previous</button>
        <button onClick={handleSubmit} disabled={submitting}
          style={{ padding:"9px 32px", background:"#1D9E75", color:"#fff", border:"none", borderRadius:6, fontSize:14, fontWeight:700, cursor:submitting?"not-allowed":"pointer", opacity:submitting?.7:1, display:"flex", alignItems:"center", gap:8 }}>
          {submitting?<><span className="spinner-border spinner-border-sm"/>Submitting...</>:"Submit"}
        </button>
      </div>
    </div>
  );
}

/* ────────────── SUCCESS ────────────── */
function SuccessScreen({ doctor, slot, onClose }) {
  return (
    <div style={{ textAlign:"center", padding:"1.5rem 0" }}>
      <div style={{ fontSize:60, marginBottom:12 }}>🎉</div>
      <h5 style={{ fontWeight:800, color:"#0A1628", marginBottom:8 }}>Appointment Booked!</h5>
      <p style={{ color:"#6B7A8D", fontSize:14, marginBottom:4 }}>Your appointment with <strong>{doctor.name}</strong> is confirmed.</p>
      <p style={{ color:"#6B7A8D", fontSize:13, marginBottom:24 }}>📅 {slot.day.displayDate} &nbsp;|&nbsp; 🕐 {slot.slot.label}</p>
      <div style={{ background:"#E1F5EE", border:"1px solid #9FE1CB", borderRadius:10, padding:"10px 16px", marginBottom:24, fontSize:13, color:"#085041" }}>
        ✅ We'll call you to confirm your appointment shortly.
      </div>
      <button onClick={onClose} style={{ padding:"11px 40px", background:"#1D9E75", color:"#fff", border:"none", borderRadius:8, fontSize:14, fontWeight:700, cursor:"pointer" }}>Close</button>
    </div>
  );
}

/* ────────────── MAIN MODAL ────────────── */
export default function BookAppointmentModal({ open, onClose }) {
  const navigate             = useNavigate();
  const { isLoggedIn, user } = useUserAuth();

  const [step,         setStep]         = useState(1);
  const [doctor,       setDoctor]       = useState(null);
  const [selectedDay,  setSelectedDay]  = useState(WEEK_DAYS[0]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [success,      setSuccess]      = useState(false);
  const [tabWarning,   setTabWarning]   = useState("");
  
  // Use ref to track if we've already navigated
  const hasNavigated = useRef(false);

  // FIX: Defer navigate() to useEffect to avoid "cannot update component during render" error
  useEffect(() => {
    if (open && !isLoggedIn && !hasNavigated.current) {
      hasNavigated.current = true;
      // Use setTimeout to defer navigation to next tick
      setTimeout(() => {
        navigate("/login", { state:{ from:"/" } });
        onClose();
      }, 0);
    }
  }, [open, isLoggedIn, navigate, onClose]);

  const resetAndClose = () => {
    setStep(1); setDoctor(null);
    setSelectedDay(WEEK_DAYS[0]); setSelectedSlot(null);
    setSuccess(false); setTabWarning("");
    hasNavigated.current = false;
    onClose();
  };

  // Don't render if modal isn't open
  if (!open) return null;

return (
  <div
    onClick={resetAndClose}
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.5)",
      zIndex: 1050,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "1rem",
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        background: "#fff",
        borderRadius: 14,
        width: "100%",
        maxWidth: 520,
        boxShadow: "0 24px 80px rgba(0,0,0,0.3)",
        fontFamily: "'Nunito','Segoe UI',sans-serif",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "16px 24px",
          borderBottom: "1px solid #F0F4F8",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h5
          style={{
            margin: 0,
            fontWeight: 800,
            color: "#0A1628",
          }}
        >
          Book Appointment
        </h5>

        <button
          onClick={resetAndClose}
          style={{
            background: "none",
            border: "none",
            fontSize: 22,
            cursor: "pointer",
          }}
        >
          ✕
        </button>
      </div>

      <div
        style={{
          padding: "40px 24px",
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontSize: 58,
            marginBottom: 14,
          }}
        >
          📞
        </div>

        <h3
          style={{
            fontWeight: 800,
            color: "#0A1628",
            marginBottom: 10,
          }}
        >
          Online Appointment is not available now
        </h3>

        <p
          style={{
            color: "#6B7A8D",
            fontSize: 15,
            marginBottom: 22,
          }}
        >
          Please contact us on
        </p>

        <a
          href="tel:+919033811409"
          style={{
            display: "inline-block",
            padding: "12px 24px",
            background: "#1D9E75",
            color: "#fff",
            borderRadius: 10,
            textDecoration: "none",
            fontWeight: 800,
            fontSize: 18,
          }}
        >
          +91 9033811409
        </a>
      </div>
    </div>
  </div>
);

  // Don't render if not logged in (will navigate via useEffect)
  if (!isLoggedIn) return null;

  const handleDoctorSelect = (doc) => {
    setDoctor(doc); setSelectedSlot(null); setTabWarning(""); setStep(2);
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot); setTabWarning(""); setStep(3);
  };

  const handleDayChange = (day) => {
    setSelectedDay(day); setSelectedSlot(null);
  };

  const handleGoToStep = (n) => {
    if (n >= 2 && !doctor) {
      setTabWarning("⚠️  Please select a doctor first before choosing a date and time.");
      setStep(1); return;
    }
    if (n === 3 && !selectedSlot) {
      setTabWarning("⚠️  Please select a date and time slot before filling in your details.");
      setStep(2); return;
    }
    setTabWarning(""); setStep(n);
  };

  const slotObj = selectedSlot ? { day:selectedDay, slot:selectedSlot } : null;

  return (
    <div onClick={resetAndClose}
      style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:1050, display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem" }}>
      <div onClick={e=>e.stopPropagation()}
        style={{ background:"#fff", borderRadius:14, width:"100%", maxWidth:700, maxHeight:"92vh", overflowY:"auto", boxShadow:"0 24px 80px rgba(0,0,0,0.3)", fontFamily:"'Nunito','Segoe UI',sans-serif" }}>

        {/* Header */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"16px 24px", borderBottom:"1px solid #F0F4F8" }}>
          <h5 style={{ fontWeight:800, fontSize:18, color:"#0A1628", margin:0 }}>Book Appointment</h5>
          <button onClick={resetAndClose} style={{ background:"none", border:"none", fontSize:22, color:"#6B7A8D", cursor:"pointer", lineHeight:1, padding:0 }}>✕</button>
        </div>

        <div style={{ padding:"20px 24px" }}>
          {success ? (
            <SuccessScreen doctor={doctor} slot={slotObj} onClose={resetAndClose} />
          ) : (
            <>
              <ClinicHeader />

              <StepTabs step={step} doctor={doctor} slotObj={slotObj} onGoToStep={handleGoToStep} />

              {tabWarning && (
                <div style={{ background:"#FAEEDA", border:"1px solid #F5C4B3", borderRadius:8, padding:"10px 14px", marginBottom:16, fontSize:13, color:"#633806", display:"flex", alignItems:"center", gap:8 }}>
                  {tabWarning}
                </div>
              )}

              {step===1 && <Step1DoctorList selectedDoctor={doctor} onSelect={handleDoctorSelect} />}
              {step===2 && <Step2DateSlot doctor={doctor} selectedDay={selectedDay} selectedSlot={selectedSlot} onDayChange={handleDayChange} onSlotSelect={handleSlotSelect} onPrev={() => { setTabWarning(""); setStep(1); }} />}
              {step===3 && slotObj && <Step3Details doctor={doctor} slot={slotObj} user={user} onPrev={() => { setTabWarning(""); setStep(2); }} onSubmit={() => setSuccess(true)} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}